import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';

import Box from '@mui/material/Box';

import { getItemDetailString } from 'bento-ordering/menus/utils/getItemDetailString';
import { MENU_ITEM_PROPTYPES } from 'bento-ordering/base/constants/propTypes';
import {
  PER_PERSON,
  ITEM_QUANTITY as STANDARD
} from 'catering/constants/quantityHandlingMethod';
import { getMenuItemNamesLighterColor } from 'styles/helpers/getThemeStyles';
import MenuItemNameTypography from 'components/MenuItemNameTypography';

import {
  ITEM_DESCRIPTION_AND_PRICES,
  MENU_ITEM_NAMES
} from 'styles/constants/appearanceSectionNames';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';

const StyledBox = styled(Box)(({ theme }) => ({
  borderColor: getMenuItemNamesLighterColor(theme)
}));
class MenuItemDescription extends React.Component {
  static propTypes = {
    ...MENU_ITEM_PROPTYPES,
    shouldRenderDescriptionBorder: PropTypes.bool,
    minimumQuantity: PropTypes.number,
    productHasMultipleSizes: PropTypes.bool,
    productHasSurchargeModifier: PropTypes.bool
  };

  static defaultProps = {
    shouldRenderDescriptionBorder: true,
    productHasMultipleSizes: false,
    productHasSurchargeModifier: false
  };

  renderDietaryText = () => {
    const {
      contains_sugar,
      dairy_free_option,
      halal,
      keto,
      keto_option,
      kosher,
      kosher_option,
      low_fodmap,
      low_sodium,
      non_alcoholic,
      organic,
      organic_option,
      paleo,
      paleo_option,
      plant_based,
      plant_based_option,
      spicy,
      spicy_hot,
      spicy_medium,
      spicy_mild,
      sugar_free,
      uncooked,
      vegan,
      vegan_option,
      vegetarian,
      vegetarian_option
    } = this.props;

    return getItemDetailString([
      { 'Contains Sugar': contains_sugar },
      { 'Dairy Free Option': dairy_free_option },
      { Halal: halal },
      { Keto: keto },
      { 'Keto Option': keto_option },
      { Kosher: kosher },
      { 'Kosher Option': kosher_option },
      { 'Low FODMAP': low_fodmap },
      { 'Low Sodium': low_sodium },
      { 'Non-Alcoholic': non_alcoholic },
      { Organic: organic },
      { 'Organic Option': organic_option },
      { Paleo: paleo },
      { 'Paleo Option': paleo_option },
      { 'Plant Based': plant_based },
      { 'Plant Based Option': plant_based_option },
      { Spicy: spicy },
      { Hot: spicy_hot },
      { Medium: spicy_medium },
      { Mild: spicy_mild },
      { 'Sugar Free': sugar_free },
      { Uncooked: uncooked },
      { Vegan: vegan },
      { 'Vegan Option': vegan_option },
      { Vegetarian: vegetarian },
      { 'Vegetarian Option': vegetarian_option }
    ]);
  };

  renderAllergensText = () => {
    const {
      cooked_to_order,
      corn,
      corn_free,
      dairy,
      dairy_free,
      egg,
      egg_free,
      fish,
      fish_free,
      gluten,
      gluten_free,
      nuts,
      nuts_free,
      peanut,
      peanut_free,
      raw,
      raw_egg,
      sesame,
      sesame_free,
      shellfish,
      shellfish_free,
      soy,
      soy_free,
      tree_nut,
      tree_nut_free,
      undercooked,
      wheat,
      wheat_free
    } = this.props;

    const allergens = [
      { 'Contains Raw Egg': raw_egg },
      { 'Cooked to Order': cooked_to_order },
      { Corn: corn },
      { 'Corn Free': corn_free },
      { Dairy: dairy },
      { 'Dairy Free': dairy_free },
      { Egg: egg },
      { 'Egg Free': egg_free },
      { Fish: fish },
      { 'Fish Free': fish_free },
      { Gluten: gluten },
      { 'Gluten Free': gluten_free },
      { Nuts: nuts },
      { 'Nuts Free': nuts_free },
      { Peanut: peanut },
      { 'Peanut Free': peanut_free },
      { Raw: raw },
      { Sesame: sesame },
      { 'Sesame Free': sesame_free },
      { Shellfish: shellfish },
      { 'Shellfish Free': shellfish_free },
      { Soy: soy },
      { 'Soy Free': soy_free },
      { 'Tree Nut': tree_nut },
      { 'Tree Nut Free': tree_nut_free },
      { Undercooked: undercooked },
      { Wheat: wheat },
      { 'Wheat Free': wheat_free }
    ];

    return allergens
      .filter(allergen => allergen[Object.keys(allergen)[0]])
      .map(allergen => Object.keys(allergen)[0])
      .join(', ')
      .trim(', ');
  };

  getPriceValue = () => {
    const {
      quantity_handling_method: quantityHandlingMethod,
      default_price,
      minimumQuantity,
      productHasMultipleSizes,
      productHasSurchargeModifier
    } = this.props;

    let display_price = parseFloat(default_price);

    if (quantityHandlingMethod === STANDARD && minimumQuantity) {
      display_price *= minimumQuantity;
    }

    let suffix = '';
    if (quantityHandlingMethod === PER_PERSON) {
      suffix = '/person';
    } else if (productHasMultipleSizes || productHasSurchargeModifier) {
      suffix = ' +';
    }

    return `$${display_price.toFixed(2)}${suffix}`;
  };

  render() {
    const {
      name,
      description,
      default_price,
      shouldRenderDescriptionBorder
    } = this.props;

    const itemNameAndPriceStyles = { variant: MENU_ITEM_NAMES };

    const itemDescriptionStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };
    const allergens = this.renderAllergensText();
    const dietary = this.renderDietaryText();
    return (
      <StyledBox borderBottom={shouldRenderDescriptionBorder ? 1 : 0}>
        <Box p={2.5}>
          <Box display="flex">
            <Box>
              <MenuItemNameTypography
                component="h2"
                {...itemNameAndPriceStyles}
              >
                {name}
              </MenuItemNameTypography>
            </Box>
            {default_price && (
              <Box textAlign="right" flexGrow={1}>
                <MenuItemNameTypography
                  component="h2"
                  {...itemNameAndPriceStyles}
                >
                  {this.getPriceValue()}
                </MenuItemNameTypography>
              </Box>
            )}
          </Box>
          {description && (
            <Box pt={1} pb={1}>
              <MenuItemDescriptionTypography
                sx={{ whiteSpace: 'pre-line' }}
                {...itemDescriptionStyles}
              >
                {description}
              </MenuItemDescriptionTypography>
            </Box>
          )}
          {allergens && (
            <Box data-testid="allergens">
              <MenuItemDescriptionTypography
                component="span"
                {...itemDescriptionStyles}
              >
                {allergens}
              </MenuItemDescriptionTypography>
            </Box>
          )}
          {dietary && (
            <Box data-testid="dietary-type" pt={1}>
              <MenuItemDescriptionTypography
                component="span"
                {...itemDescriptionStyles}
              >
                {dietary}
              </MenuItemDescriptionTypography>
            </Box>
          )}
        </Box>
      </StyledBox>
    );
  }
}

export default MenuItemDescription;
